import React, { useEffect } from 'react';
import { LOADER_TYPES } from '../../../data/loaderTypes';

const LoaderPreview = ({ config }) => {
  useEffect(() => {
    // Auto-animate the loader - config changes will trigger re-render
  }, [config]);

  const renderLoader = () => {
    const { style, size, primaryColor, secondaryColor, speed, strokeWidth } = config;

    switch (style) {
      case LOADER_TYPES.RING:
        return (
          <div className="relative">
            <svg
              width={size}
              height={size}
              className="animate-spin"
              style={{
                animationDuration: `${speed}s`,
              }}
            >
              <circle
                cx={size / 2}
                cy={size / 2}
                r={size / 2 - strokeWidth / 2}
                fill="none"
                stroke={`url(#gradient-${style})`}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={`${Math.PI * (size - strokeWidth) * 0.7} ${Math.PI * (size - strokeWidth)}`}
              />
              <defs>
                <linearGradient id={`gradient-${style}`} x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={primaryColor} />
                  <stop offset="100%" stopColor={secondaryColor} />
                </linearGradient>
              </defs>
            </svg>
          </div>
        );

      case LOADER_TYPES.PULSE: {
        const { minSize = 30, maxSize = 50, useGradient = false } = config;
        const pulseStyle = {
          width: `${minSize}px`,
          height: `${minSize}px`,
          borderRadius: '50%',
          animation: `pulseSize ${speed}s ease-in-out infinite`,
        };
        
        if (useGradient) {
          pulseStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
        } else {
          pulseStyle.backgroundColor = primaryColor;
        }
        
        return (
          <>
            <div className="rounded-full" style={pulseStyle} />
            <style>{`
              @keyframes pulseSize {
                0%, 100% {
                  width: ${minSize}px;
                  height: ${minSize}px;
                  opacity: 0.7;
                }
                50% {
                  width: ${maxSize}px;
                  height: ${maxSize}px;
                  opacity: 1;
                }
              }
            `}</style>
          </>
        );
      }

      case LOADER_TYPES.DOTS: {
        const { dotCount = 3, minDotSize = 8, maxDotSize = 20, useGradient = false } = config;
        const dotArray = Array.from({ length: dotCount }, (_, i) => i);
        const gap = Math.max(4, (size - maxDotSize * dotCount) / (dotCount + 1));
        
        return (
          <div className="flex items-center" style={{ gap: `${gap}px` }}>
            {dotArray.map((index) => {
              const dotStyle = {
                width: `${minDotSize}px`,
                height: `${minDotSize}px`,
                borderRadius: '50%',
                animation: `dotPulse ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / dotCount)}s`,
              };
              
              if (useGradient) {
                dotStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                dotStyle.backgroundColor = primaryColor;
              }
              
              return (
                <div
                  key={index}
                  className="rounded-full"
                  style={dotStyle}
                />
              );
            })}
            <style>{`
              @keyframes dotPulse {
                0%, 100% {
                  width: ${minDotSize}px;
                  height: ${minDotSize}px;
                }
                50% {
                  width: ${maxDotSize}px;
                  height: ${maxDotSize}px;
                }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.BARS: {
        const { barCount = 5, barWidth = 4, barHeight = 30, barSpacing = 4, useGradient = false } = config;
        const bars = Array.from({ length: barCount }, (_, i) => i);
        
        return (
          <div className="flex items-end" style={{ gap: `${barSpacing}px` }}>
            {bars.map((index) => {
              const barStyle = {
                width: `${barWidth}px`,
                height: `${barHeight}px`,
                animation: `barBounce ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / barCount)}s`,
              };
              
              if (useGradient) {
                barStyle.background = `linear-gradient(180deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                barStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={barStyle} />;
            })}
            <style>{`
              @keyframes barBounce {
                0%, 100% { height: ${barHeight * 0.3}px; opacity: 0.5; }
                50% { height: ${barHeight}px; opacity: 1; }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.WAVE: {
        const { waveCount = 4, waveHeight = 20, waveWidth = 6, useGradient = false } = config;
        const waves = Array.from({ length: waveCount }, (_, i) => i);
        
        return (
          <div className="flex items-center" style={{ gap: '4px' }}>
            {waves.map((index) => {
              const waveStyle = {
                width: `${waveWidth}px`,
                height: `${waveHeight}px`,
                borderRadius: `${waveWidth / 2}px`,
                animation: `waveAnimation ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / waveCount)}s`,
              };
              
              if (useGradient) {
                waveStyle.background = `linear-gradient(180deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                waveStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={waveStyle} />;
            })}
            <style>{`
              @keyframes waveAnimation {
                0%, 100% { height: ${waveHeight * 0.3}px; }
                50% { height: ${waveHeight}px; }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.SPINNER: {
        const { armCount = 8, armLength = 20, useGradient = false } = config;
        const center = size / 2;
        const arms = Array.from({ length: armCount }, (_, i) => i);
        const angleStep = 360 / armCount;
        
        return (
          <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="animate-spin" style={{ animationDuration: `${speed}s` }}>
              <defs>
                {useGradient ? (
                  <linearGradient id="spinnerGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={primaryColor} />
                    <stop offset="100%" stopColor={secondaryColor} />
                  </linearGradient>
                ) : null}
              </defs>
              {arms.map((index) => {
                const angle = index * angleStep;
                const x1 = center;
                const y1 = center;
                const x2 = center + Math.cos((angle - 90) * Math.PI / 180) * armLength;
                const y2 = center + Math.sin((angle - 90) * Math.PI / 180) * armLength;
                
                return (
                  <line
                    key={index}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={useGradient ? 'url(#spinnerGradient)' : primaryColor}
                    strokeWidth="3"
                    strokeLinecap="round"
                    opacity={0.3 + (index / armCount) * 0.7}
                  />
                );
              })}
            </svg>
          </div>
        );
      }

      case LOADER_TYPES.PROGRESS_RING: {
        const { progress = 0.7 } = config;
        const radius = size / 2 - strokeWidth / 2;
        const circumference = 2 * Math.PI * radius;
        const offset = circumference * (1 - progress);
        
        return (
          <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={secondaryColor}
                strokeWidth={strokeWidth}
                opacity="0.2"
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={`url(#progressGradient)`}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{
                  transition: 'stroke-dashoffset 0.3s ease',
                }}
              />
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={primaryColor} />
                  <stop offset="100%" stopColor={secondaryColor} />
                </linearGradient>
              </defs>
            </svg>
          </div>
        );
      }

      case LOADER_TYPES.SKELETON: {
        const { skeletonLineCount = 3, skeletonLineHeight = 12, skeletonLineSpacing = 8 } = config;
        const lines = Array.from({ length: skeletonLineCount }, (_, i) => i);
        
        return (
          <div className="flex flex-col" style={{ gap: `${skeletonLineSpacing}px`, width: '200px' }}>
            {lines.map((index) => (
              <div
                key={index}
                className="rounded"
                style={{
                  height: `${skeletonLineHeight}px`,
                  backgroundColor: primaryColor,
                  animation: `skeletonShimmer ${speed}s ease-in-out infinite`,
                  animationDelay: `${index * 0.1}s`,
                  width: index === lines.length - 1 ? '60%' : '100%',
                }}
              />
            ))}
            <style>{`
              @keyframes skeletonShimmer {
                0%, 100% { opacity: 0.6; }
                50% { opacity: 1; }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.BOUNCE: {
        const { dotCount = 3, dotSize = 12, bounceHeight = 20, useGradient = false } = config;
        const dots = Array.from({ length: dotCount }, (_, i) => i);
        
        return (
          <div className="flex items-end" style={{ gap: '8px' }}>
            {dots.map((index) => {
              const dotStyle = {
                width: `${dotSize}px`,
                height: `${dotSize}px`,
                borderRadius: '50%',
                animation: `bounce ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / dotCount)}s`,
              };
              
              if (useGradient) {
                dotStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                dotStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={dotStyle} />;
            })}
            <style>{`
              @keyframes bounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-${bounceHeight}px); }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.ROTATE: {
        const { elementCount = 6, elementSize = 8, useGradient = false } = config;
        const center = size / 2;
        const radius = size / 3;
        const elements = Array.from({ length: elementCount }, (_, i) => i);
        const angleStep = 360 / elementCount;
        
        return (
          <div className="relative" style={{ width: size, height: size }}>
            {elements.map((index) => {
              const angle = index * angleStep;
              const x = center + Math.cos((angle - 90) * Math.PI / 180) * radius - elementSize / 2;
              const y = center + Math.sin((angle - 90) * Math.PI / 180) * radius - elementSize / 2;
              
              const elementStyle = {
                position: 'absolute',
                width: `${elementSize}px`,
                height: `${elementSize}px`,
                borderRadius: '50%',
                left: `${x}px`,
                top: `${y}px`,
                animation: `rotateElements ${speed}s linear infinite`,
                animationDelay: `${index * (speed / elementCount)}s`,
              };
              
              if (useGradient) {
                elementStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                elementStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={elementStyle} />;
            })}
            <style>{`
              @keyframes rotateElements {
                from { transform: rotate(0deg) translateX(${size / 3}px) rotate(0deg); }
                to { transform: rotate(360deg) translateX(${size / 3}px) rotate(-360deg); }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.FADE: {
        const { elementCount = 4, elementSize = 12, useGradient = false } = config;
        const elements = Array.from({ length: elementCount }, (_, i) => i);
        
        return (
          <div className="flex items-center" style={{ gap: '8px' }}>
            {elements.map((index) => {
              const elementStyle = {
                width: `${elementSize}px`,
                height: `${elementSize}px`,
                borderRadius: '50%',
                animation: `fadeInOut ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / elementCount)}s`,
              };
              
              if (useGradient) {
                elementStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                elementStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={elementStyle} />;
            })}
            <style>{`
              @keyframes fadeInOut {
                0%, 100% { opacity: 0.2; }
                50% { opacity: 1; }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.SCALE: {
        const { elementCount = 3, minScale = 0.5, maxScale = 1.0, useGradient = false } = config;
        const elements = Array.from({ length: elementCount }, (_, i) => i);
        
        return (
          <div className="flex items-center" style={{ gap: '8px' }}>
            {elements.map((index) => {
              const elementStyle = {
                width: `${size / elementCount}px`,
                height: `${size / elementCount}px`,
                borderRadius: '50%',
                animation: `scaleAnimation ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / elementCount)}s`,
              };
              
              if (useGradient) {
                elementStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                elementStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={elementStyle} />;
            })}
            <style>{`
              @keyframes scaleAnimation {
                0%, 100% { transform: scale(${minScale}); }
                50% { transform: scale(${maxScale}); }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.RIPPLE: {
        const { rippleCount = 3, rippleWidth = 2, useGradient = false } = config;
        const ripples = Array.from({ length: rippleCount }, (_, i) => i);
        
        return (
          <div className="relative" style={{ width: size, height: size }}>
            {ripples.map((index) => {
              const rippleStyle = {
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: `${size * 0.3}px`,
                height: `${size * 0.3}px`,
                marginLeft: `-${size * 0.15}px`,
                marginTop: `-${size * 0.15}px`,
                borderRadius: '50%',
                border: `${rippleWidth}px solid ${useGradient ? primaryColor : primaryColor}`,
                animation: `ripple ${speed}s ease-out infinite`,
                animationDelay: `${index * (speed / rippleCount)}s`,
                opacity: 0.6,
              };
              
              return <div key={index} style={rippleStyle} />;
            })}
            <style>{`
              @keyframes ripple {
                0% {
                  transform: scale(0.8);
                  opacity: 0.8;
                }
                100% {
                  transform: scale(2);
                  opacity: 0;
                }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.ORBIT: {
        const { orbitCount = 3, orbitSize = 8, orbitRadius = 20, useGradient = false } = config;
        const center = size / 2;
        const orbits = Array.from({ length: orbitCount }, (_, i) => i);
        
        return (
          <div className="relative" style={{ width: size, height: size }}>
            {orbits.map((index) => {
              const orbitStyle = {
                position: 'absolute',
                width: `${orbitSize}px`,
                height: `${orbitSize}px`,
                borderRadius: '50%',
                left: `${center - orbitSize / 2}px`,
                top: `${center - orbitSize / 2}px`,
                animation: `orbit ${speed}s linear infinite`,
                animationDelay: `${index * (speed / orbitCount)}s`,
              };
              
              if (useGradient) {
                orbitStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                orbitStyle.backgroundColor = primaryColor;
              }
              
              return (
                <div
                  key={index}
                  style={orbitStyle}
                />
              );
            })}
            <style>{`
              @keyframes orbit {
                from {
                  transform: rotate(0deg) translateX(${orbitRadius}px) rotate(0deg);
                }
                to {
                  transform: rotate(360deg) translateX(${orbitRadius}px) rotate(-360deg);
                }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.GRID: {
        const { gridRows = 3, gridCols = 3, cellSize = 10, useGradient = false } = config;
        const totalCells = gridRows * gridCols;
        const cells = Array.from({ length: totalCells }, (_, i) => i);
        
        return (
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${gridCols}, ${cellSize}px)`,
              gap: '4px',
            }}
          >
            {cells.map((index) => {
              const cellStyle = {
                width: `${cellSize}px`,
                height: `${cellSize}px`,
                animation: `gridPulse ${speed}s ease-in-out infinite`,
                animationDelay: `${(index % totalCells) * (speed / totalCells)}s`,
              };
              
              if (useGradient) {
                cellStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                cellStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={cellStyle} />;
            })}
            <style>{`
              @keyframes gridPulse {
                0%, 100% { opacity: 0.3; transform: scale(0.8); }
                50% { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.LINES: {
        const { lineCount = 5, lineWidth = 3, lineLength = 30, useGradient = false } = config;
        const lines = Array.from({ length: lineCount }, (_, i) => i);
        
        return (
          <div className="flex flex-col items-center" style={{ gap: '4px' }}>
            {lines.map((index) => {
              const lineStyle = {
                width: `${lineLength}px`,
                height: `${lineWidth}px`,
                borderRadius: `${lineWidth / 2}px`,
                animation: `lineAnimation ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / lineCount)}s`,
              };
              
              if (useGradient) {
                lineStyle.background = `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                lineStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={lineStyle} />;
            })}
            <style>{`
              @keyframes lineAnimation {
                0%, 100% { opacity: 0.3; transform: scaleX(0.5); }
                50% { opacity: 1; transform: scaleX(1); }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.SHIMMER: {
        const { width = 200, height = 20, shimmerColor = '#ffffff' } = config;
        
        return (
          <div
            className="relative rounded overflow-hidden"
            style={{
              width: `${width}px`,
              height: `${height}px`,
              backgroundColor: primaryColor,
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(90deg, transparent, ${shimmerColor}40, transparent)`,
                animation: `shimmer ${speed}s ease-in-out infinite`,
              }}
            />
            <style>{`
              @keyframes shimmer {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(100%); }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.STAGGER: {
        const { elementCount = 5, elementSize = 10, staggerDelay = 0.1, useGradient = false } = config;
        const elements = Array.from({ length: elementCount }, (_, i) => i);
        
        return (
          <div className="flex items-center" style={{ gap: '6px' }}>
            {elements.map((index) => {
              const elementStyle = {
                width: `${elementSize}px`,
                height: `${elementSize}px`,
                borderRadius: '50%',
                animation: `staggerBounce ${speed}s ease-in-out infinite`,
                animationDelay: `${index * staggerDelay}s`,
              };
              
              if (useGradient) {
                elementStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                elementStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={elementStyle} />;
            })}
            <style>{`
              @keyframes staggerBounce {
                0%, 100% { transform: translateY(0) scale(1); opacity: 0.6; }
                50% { transform: translateY(-10px) scale(1.2); opacity: 1; }
              }
            `}</style>
          </div>
        );
      }

      case LOADER_TYPES.MORPH: {
        const { shapeCount = 4, useGradient = false } = config;
        const center = size / 2;
        const radius = size / 3;
        
        return (
          <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="animate-spin" style={{ animationDuration: `${speed}s` }}>
              <defs>
                {useGradient ? (
                  <linearGradient id="morphGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={primaryColor} />
                    <stop offset="100%" stopColor={secondaryColor} />
                  </linearGradient>
                ) : null}
              </defs>
              {Array.from({ length: shapeCount }, (_, i) => {
                const angle = (i * 360) / shapeCount;
                const x = center + Math.cos((angle - 90) * Math.PI / 180) * radius;
                const y = center + Math.sin((angle - 90) * Math.PI / 180) * radius;
                
                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r="6"
                    fill={useGradient ? 'url(#morphGradient)' : primaryColor}
                    opacity={0.3 + (i / shapeCount) * 0.7}
                  />
                );
              })}
            </svg>
          </div>
        );
      }

      case LOADER_TYPES.PARTICLE: {
        const { particleCount = 12, particleSize = 4, useGradient = false } = config;
        const center = size / 2;
        const particles = Array.from({ length: particleCount }, (_, i) => i);
        
        return (
          <div className="relative" style={{ width: size, height: size }}>
            {particles.map((index) => {
              const angle = (index * 360) / particleCount;
              const distance = size / 3;
              const x = center + Math.cos((angle - 90) * Math.PI / 180) * distance;
              const y = center + Math.sin((angle - 90) * Math.PI / 180) * distance;
              
              const particleStyle = {
                position: 'absolute',
                width: `${particleSize}px`,
                height: `${particleSize}px`,
                borderRadius: '50%',
                left: `${x - particleSize / 2}px`,
                top: `${y - particleSize / 2}px`,
                animation: `particleFloat ${speed}s ease-in-out infinite`,
                animationDelay: `${index * (speed / particleCount)}s`,
              };
              
              if (useGradient) {
                particleStyle.background = `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`;
              } else {
                particleStyle.backgroundColor = primaryColor;
              }
              
              return <div key={index} style={particleStyle} />;
            })}
            <style>{`
              @keyframes particleFloat {
                0%, 100% {
                  transform: translate(0, 0) scale(1);
                  opacity: 0.6;
                }
                50% {
                  transform: translate(${Math.cos(45) * 5}px, ${Math.sin(45) * 5}px) scale(1.3);
                  opacity: 1;
                }
              }
            `}</style>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center" style={{ minWidth: 0, minHeight: 0 }}>
      {config.showBackground && (
        <div
          className="absolute inset-0 bg-black"
          style={{ opacity: config.backgroundOpacity }}
        />
      )}
      <div className="relative z-10 flex items-center justify-center">{renderLoader()}</div>
    </div>
  );
};

export default LoaderPreview;

